import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  formatTL,
  createEmptyTazminatForm,
} from '../utils/tazminatHesap';
import { isGucuKaybiHesapla, destekHesapla, donemOzeti } from '../utils/tazminatMotoru';
import { getAsgariNet, getGuncelAsgariNet } from '../utils/asgariUcret';
import { caseFileAPI } from '../api/api';
import { enqueueDocumentUploads } from '../utils/backgroundUploads';
import { DOCUMENT_TYPE } from '../utils/constants';
import TazminatDosyaDoldur from '../components/Tazminat/TazminatDosyaDoldur';
import './TazminatHesap.css';

/** PDF'in yerleşik yazı tipi Türkçe harf basamıyor; sadeleştir. */
const ascii = (s) => String(s ?? '')
  .replace(/ı/g, 'i').replace(/İ/g, 'I').replace(/ş/g, 's').replace(/Ş/g, 'S').replace(/ğ/g, 'g').replace(/Ğ/g, 'G')
  .replace(/ü/g, 'u').replace(/Ü/g, 'U').replace(/ö/g, 'o').replace(/Ö/g, 'O').replace(/ç/g, 'c').replace(/Ç/g, 'C');

/** "2026-10-04" → "04.10.2026" */
const trTarih = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '-');

/** 34.46 → "34,46" */
const yilYaz = (n) => Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

const fileLabelOf = (f) => {
  if (!f) return '';
  const foy = f.FoyNo ?? f.foyNo ?? '';
  const name = f.Muvekkil ?? f.muvekkil ?? '';
  return [foy, name].filter(Boolean).join(' — ');
};

const buildTazminatPdfBlob = ({ form, result, fileLabel }) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const title = form.mod === 'destek' ? 'Destek Tazminati Hesabi' : 'Is Gucu Kaybi Hesabi';
  doc.setFontSize(14);
  doc.text(title, 14, 16);
  doc.setFontSize(9);
  doc.text(`Dosya: ${fileLabel || '-'}`, 14, 22);
  if (form.adSoyad) doc.text(`Ad: ${form.adSoyad}`, 14, 27);

  const info = [
    ['Olay tarihi', form.olayTarihi || '-'],
    ['Dogum tarihi', form.dogumTarihi || '-'],
    ['Rapor / hesap tarihi', form.raporTarihi || '-'],
    ['Davali kusuru %', String(form.davaliKusur ?? '')],
    ['Aylik ucret', form.aylikUcret === '' || form.aylikUcret == null ? 'Asgari (otomatik)' : String(form.aylikUcret)],
    ['Hesap yontemi', form.hesapYontemi === 'aktueryal' ? 'Aktueryal' : 'Progresif rant'],
    ['Artis / iskonto %', `${form.artisOrani} / ${form.iskontoOrani}`],
  ];
  if (form.mod !== 'destek') {
    info.push(['Is gucu kayip %', String(form.isGucuKayip ?? '')]);
    if (form.sigortaOdemesi) info.push(['Sigorta odemesi', String(form.sigortaOdemesi)]);
  }

  autoTable(doc, {
    startY: 32,
    head: [['Parametre', 'Deger']],
    body: info,
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [56, 70, 106], textColor: 255 },
    columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 'auto' } }
  });

  const rows = result.type === 'destek'
    ? [
        ['Bilinen donem (toplam)', formatTL(result.gecmis)],
        ['Gelecek donem (toplam)', formatTL(result.gelecek)],
        ...((result.dagilim || []).map((d) => [
          `${d.ad} (destek ${trTarih(d.destekBitis)} tarihine kadar${d.evlenmeIhtimali ? `, evlenme %${d.evlenmeIhtimali}` : ''}${d.sgkPsd ? ', SGK PSD dusuldu' : ''})`,
          formatTL(d.toplam),
        ])),
        ['GENEL TOPLAM', formatTL(result.genelToplam)],
      ]
    : [
        ['Gecmis donem', formatTL(result.gecmis)],
        ['Gelecek (aktif)', formatTL(result.gelecekAktif)],
        ['Gelecek (pasif)', formatTL(result.gelecekPasif)],
        ...(result.geciciIsgoremezlik > 0 ? [['Gecici isgoremezlik', formatTL(result.geciciIsgoremezlik)]] : []),
        ...(result.bakiciGideri > 0 ? [['Bakici gideri', formatTL(result.bakiciGideri)]] : []),
        ...(result.sigortaMahsup > 0 ? [['Sigorta mahsup (-)', formatTL(result.sigortaMahsup)]] : []),
        ['TOPLAM', formatTL(result.toplam)],
      ];

  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || 80) + 8,
    head: [['Kalem', 'Tutar']],
    body: rows.map(([a, b]) => [ascii(a), b]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [56, 70, 106], textColor: 255 },
    columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 'auto', halign: 'right' } }
  });

  // Dönem dönem hesap tablosu (bilirkişi raporlarındaki gibi)
  if (result.satirlar?.length) {
    const destek = result.type === 'destek';
    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 120) + 8,
      head: [destek
        ? ['Donem', 'Ay', 'Aylik gelir', ...result.dagilim.map((d) => ascii(d.ad))]
        : ['Kalem', 'Donem', 'Ay', 'Aylik gelir', 'Kayip %', 'Tutar']],
      body: result.satirlar.map((s) => (destek
        ? [`${trTarih(s.bas)} - ${trTarih(s.son)}${s.pasif ? ' (pasif)' : ''}`, String(s.ay), formatTL(s.aylik),
          ...result.dagilim.map((d) => (s.paylar[d.sira] ? `${formatTL(s.paylar[d.sira].tutar)} (${s.paylar[d.sira].pay})` : '-'))]
        : [ascii(s.tur), `${trTarih(s.bas)} - ${trTarih(s.son)}`, String(s.ay), formatTL(s.aylik), String(s.oran), formatTL(s.tutar)])),
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [56, 70, 106], textColor: 255 },
    });
    doc.setFontSize(7);
    doc.text('Donem tablosu kusur, evlenme ve SGK indiriminden oncedir.', 14, (doc.lastAutoTable?.finalY || 200) + 5);
  }

  const stamp = new Date();
  const ymd = `${stamp.getFullYear()}${String(stamp.getMonth() + 1).padStart(2, '0')}${String(stamp.getDate()).padStart(2, '0')}`;
  return {
    blob: doc.output('blob'),
    fileName: `Tazminat_Hesabi_${ymd}.pdf`
  };
};

const TazminatHesap = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => createEmptyTazminatForm());
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [caseFiles, setCaseFiles] = useState([]);
  const [caseQuery, setCaseQuery] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState(searchParams.get('caseFileId') || '');
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      caseFileAPI.getAll(false).catch(() => []),
      caseFileAPI.getAll(true).catch(() => [])
    ]).then(([normal, ozel]) => {
      if (cancelled) return;
      const map = new Map();
      [...(normal || []), ...(ozel || [])].forEach((f) => {
        const id = f.Id ?? f.id;
        if (id) map.set(id, f);
      });
      setCaseFiles([...map.values()]);
    });
    return () => { cancelled = true; };
  }, []);

  const olayAsgariHint = form.olayTarihi ? getAsgariNet(form.olayTarihi) : getGuncelAsgariNet();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const setMod = (mod) => {
    setForm((prev) => ({ ...prev, mod }));
    setResult(null);
    setError('');
  };

  const updateCocuk = (index, field, value) => {
    setForm((prev) => {
      const cocuklar = [...prev.cocuklar];
      cocuklar[index] = { ...cocuklar[index], [field]: value };
      return { ...prev, cocuklar };
    });
  };

  const addCocuk = () => {
    setForm((prev) => {
      if (prev.cocuklar.length >= 5) return prev;
      return {
        ...prev,
        cocuklar: [...prev.cocuklar, { dogumTarihi: '', cinsiyet: 'E', yuksekOgrenim: false }],
      };
    });
  };

  const removeCocuk = (index) => {
    setForm((prev) => ({
      ...prev,
      cocuklar: prev.cocuklar.filter((_, i) => i !== index),
    }));
  };

  const periodsPreview = useMemo(() => {
    try {
      if (!form.olayTarihi || !form.dogumTarihi || !form.raporTarihi) return null;
      return donemOzeti({
        olayTarihi: form.olayTarihi,
        raporTarihi: form.raporTarihi,
        dogumTarihi: form.dogumTarihi,
        cinsiyet: form.cinsiyet,
        aktifBaslangicYas: form.aktifBaslangicYas,
        aktifBitisYas: form.aktifBitisYas,
        tumuPasif: !!form.geliriYok,
      });
    } catch {
      return null;
    }
  }, [form.olayTarihi, form.dogumTarihi, form.raporTarihi, form.cinsiyet, form.aktifBaslangicYas, form.aktifBitisYas, form.geliriYok]);

  const handleReset = () => {
    setForm(createEmptyTazminatForm());
    setResult(null);
    setError('');
  };

  const handleHesapla = (e) => {
    e.preventDefault();
    setError('');
    if (!form.olayTarihi || !form.dogumTarihi || !form.raporTarihi) {
      setError('Olay tarihi, doğum tarihi ve rapor tarihi zorunludur.');
      return;
    }
    if (form.aylikUcret !== '' && form.aylikUcret != null && Number(form.aylikUcret) < 0) {
      setError('Aylık ücret negatif olamaz. Boş bırakırsanız olay tarihindeki asgari ücret kullanılır.');
      return;
    }

    // Aktüeryal seçiliyse gelir artırılmaz, teknik faizle iskonto edilir.
    const aktueryal = form.hesapYontemi === 'aktueryal';
    const common = {
      olayTarihi: form.olayTarihi,
      raporTarihi: form.raporTarihi,
      dogumTarihi: form.dogumTarihi,
      cinsiyet: form.cinsiyet,
      aylikUcret: form.aylikUcret,
      davaliKusur: form.davaliKusur,
      artisOrani: aktueryal ? 0 : form.artisOrani,
      iskontoOrani: aktueryal ? form.teknikFaiz : form.iskontoOrani,
      aktifBaslangicYas: form.aktifBaslangicYas,
      aktifBitisYas: form.aktifBitisYas,
      pasifAylik: form.pasifAylikUcret === '' ? null : form.pasifAylikUcret,
      tumuPasif: !!form.geliriYok,
    };

    if (form.mod === 'destek') {
      const hakSahipleri = [
        ...(form.esVar && form.esDogum ? [{ tip: 'es', ad: 'Eş', dogumTarihi: form.esDogum, cinsiyet: form.esCinsiyet, psd: form.esPsd }] : []),
        // (ilave AGİ yalnız 2022 öncesi dönemlere eklenir — motor ayırıyor)
        ...form.cocuklar
          .map((c, i) => ({ ...c, i }))
          .filter((c) => c.dogumTarihi)
          .map((c) => ({ tip: 'cocuk', ad: `${c.i + 1}. Çocuk`, dogumTarihi: c.dogumTarihi, cinsiyet: c.cinsiyet, yuksekOgrenim: !!c.yuksekOgrenim, psd: c.psd })),
        ...(form.anneVar && form.anneDogum ? [{ tip: 'anne', ad: 'Anne', dogumTarihi: form.anneDogum, cinsiyet: 'K', psd: form.annePsd }] : []),
        ...(form.babaVar && form.babaDogum ? [{ tip: 'baba', ad: 'Baba', dogumTarihi: form.babaDogum, cinsiyet: 'E', psd: form.babaPsd }] : []),
      ];
      if (!hakSahipleri.length) {
        setError('Destek tazminatı için en az bir hak sahibinin (eş, çocuk, anne, baba) doğum tarihini girin.');
        return;
      }
      const r = destekHesapla({ ...common, ilaveAgi: form.ilaveAgi, hakSahipleri });
      if (!r) { setError('Tarihleri kontrol edin (hesap tarihi olay tarihinden önce olamaz).'); return; }
      setResult(r);
      return;
    }

    if (form.isGucuKayip === '' || Number(form.isGucuKayip) < 0) {
      setError('İş gücü kayıp oranı giriniz.');
      return;
    }

    const r = isGucuKaybiHesapla({
      ...common,
      isGucuKayip: form.isGucuKayip,
      sigortaOdemesi: form.sigortaOdemesi,
      geciciGun: form.geciciHesapla ? form.geciciGun : 0,
      geciciOran: form.geciciOran,
      bakiciTur: form.bakiciHesapla ? form.bakiciTur : 'gun',
      bakiciGun: form.bakiciHesapla ? form.bakiciGun : 0,
      bakiciOran: form.bakiciOran,
      bakiciUcret: form.bakiciUcret,
    });
    if (!r) { setError('Tarihleri kontrol edin (hesap tarihi olay tarihinden önce olamaz).'); return; }
    setResult(r);
  };

  const filteredCases = useMemo(() => {
    const q = caseQuery.trim().toLowerCase();
    if (!q) return caseFiles.slice(0, 80);
    return caseFiles.filter((f) => {
      const foy = String(f.FoyNo ?? f.foyNo ?? '').toLowerCase();
      const name = String(f.Muvekkil ?? f.muvekkil ?? '').toLowerCase();
      return foy.includes(q) || name.includes(q);
    }).slice(0, 80);
  }, [caseFiles, caseQuery]);

  const selectedCase = caseFiles.find((f) => String(f.Id ?? f.id) === String(selectedCaseId));

  const handleAktarDosyaya = async () => {
    if (!result) {
      setExportMsg('Önce hesabı yapın.');
      return;
    }
    if (!selectedCaseId) {
      setExportMsg('Belgenin yükleneceği dosyayı seçin.');
      return;
    }
    setExporting(true);
    setExportMsg('');
    try {
      const { blob, fileName } = buildTazminatPdfBlob({
        form,
        result,
        fileLabel: fileLabelOf(selectedCase)
      });
      const file = new File([blob], fileName, { type: 'application/pdf' });
      enqueueDocumentUploads({
        caseFileId: parseInt(selectedCaseId, 10),
        files: [file],
        descriptions: { 0: form.mod === 'destek' ? 'Destek tazminatı hesabı' : 'İş gücü kaybı hesabı' },
        documentType: DOCUMENT_TYPE.Other,
        onBatchComplete: ({ successCount, failCount }) => {
          if (successCount > 0 && failCount === 0) {
            setExportMsg('PDF belgelere yüklendi.');
          } else if (failCount > 0) {
            setExportMsg('Yükleme başarısız. Sağ alttaki panelden tekrar deneyin.');
          }
        }
      });
      setExportMsg('PDF kuyruğa alındı, belgelere yükleniyor…');
    } catch (err) {
      setExportMsg('PDF oluşturulamadı: ' + (err?.message || 'bilinmeyen hata'));
    } finally {
      setExporting(false);
    }
  };

  const isDestek = form.mod === 'destek';
  const exportBar = (
    <div className="th-export">
      <div className="th-export-title">Bu hesabı dosyaya aktar</div>
      <div className="th-export-row">
        <input
          type="search"
          className="th-export-search"
          placeholder="Föy no veya müvekkil ara…"
          value={caseQuery}
          onChange={(e) => setCaseQuery(e.target.value)}
        />
        <select
          className="th-export-select"
          value={selectedCaseId}
          onChange={(e) => setSelectedCaseId(e.target.value)}
        >
          <option value="">Dosya seçin</option>
          {filteredCases.map((f) => {
            const id = f.Id ?? f.id;
            return (
              <option key={id} value={id}>{fileLabelOf(f)}</option>
            );
          })}
        </select>
        <button
          type="button"
          className="th-btn primary"
          disabled={!result || exporting}
          onClick={handleAktarDosyaya}
        >
          {exporting ? 'Aktarılıyor…' : 'Bu hesabı dosyaya aktar'}
        </button>
      </div>
      {exportMsg && <p className="th-export-msg">{exportMsg}</p>}
      {!result && <p className="th-muted">Önce hesabı yapın, sonra PDF olarak belgelere yüklenir.</p>}
    </div>
  );

  return (
    <div className="tazminat-hesap-page">
      <div className="th-header">
        <div className="th-tabs">
          <button
            type="button"
            className={`th-tab ${!isDestek ? 'active' : ''}`}
            onClick={() => setMod('isgucu')}
          >
            İş Gücü Kaybı
          </button>
          <button
            type="button"
            className={`th-tab ${isDestek ? 'active' : ''}`}
            onClick={() => setMod('destek')}
          >
            Destek Tazminatı
          </button>
        </div>
        <h1>{isDestek ? 'Destek Tazminatı Hesabı' : 'İş Gücü Kaybı Hesabı'}</h1>
        <p className="th-subtitle">
          {isDestek
            ? 'Destekten yoksun kalma — progresif rant / aktüeryal, AYİM paylaştırma (eş 2, çocuk/ana/baba 1)'
            : 'Trafik / iş kazası maluliyet — geçmiş gün×(maaş/30), gelecek yıllık×yıl (artış=iskonto)'}
        </p>
      </div>

      <form className="th-layout" onSubmit={handleHesapla}>
        <div className="th-main">
          <TazminatDosyaDoldur
            dosyalar={caseFiles}
            baslangicId={searchParams.get('caseFileId') || ''}
            onDosyaSec={setSelectedCaseId}
            onDoldur={(g) => { setForm((prev) => ({ ...prev, ...g })); setResult(null); setError(''); }}
          />
          <div className="th-config-row">
            <fieldset className="th-box">
              <legend>Kaza Türü</legend>
              <label className="th-radio">
                <input type="radio" name="kazaTuru" value="is" checked={form.kazaTuru === 'is'} onChange={handleChange} />
                İş Kazası
              </label>
              <label className="th-radio">
                <input type="radio" name="kazaTuru" value="trafik" checked={form.kazaTuru === 'trafik'} onChange={handleChange} />
                Trafik Kazası (ZMSS)
              </label>
            </fieldset>

            <fieldset className="th-box">
              <legend>Hesap Yöntemi</legend>
              <label className="th-radio">
                <input type="radio" name="hesapYontemi" value="progresif" checked={form.hesapYontemi === 'progresif'} onChange={handleChange} />
                Progresif Rant
              </label>
              <label className="th-radio">
                <input type="radio" name="hesapYontemi" value="aktueryal" checked={form.hesapYontemi === 'aktueryal'} onChange={handleChange} />
                Aktüeryal (Teknik Faiz %)
              </label>
              <div className="th-inline-field">
                <span>Teknik Faiz %</span>
                <input
                  type="number"
                  name="teknikFaiz"
                  value={form.teknikFaiz}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  disabled={form.hesapYontemi !== 'aktueryal'}
                />
              </div>
            </fieldset>

            <fieldset className="th-box">
              <legend>Yaşam Tablosu</legend>
              <label className="th-radio">
                <input type="radio" name="yasamTablosu" value="TRH-2010" checked={form.yasamTablosu === 'TRH-2010'} onChange={handleChange} />
                TRH-2010
              </label>
              <label className="th-radio">
                <input type="radio" name="yasamTablosu" value="PMF-1931" checked={form.yasamTablosu === 'PMF-1931'} onChange={handleChange} />
                PMF-1931
              </label>
              <label className="th-radio">
                <input type="radio" name="yasamTablosu" value="CSO-1980" checked={form.yasamTablosu === 'CSO-1980'} onChange={handleChange} />
                CSO-1980
              </label>
              {form.yasamTablosu !== 'TRH-2010' && (
                <p className="th-note">Bakiye ömür şu an TRH-2010 ile hesaplanır.</p>
              )}
            </fieldset>
          </div>

          <div className="th-inputs-grid">
            <div className="th-col">
              <label>
                Davalı Kusuru %
                <input type="number" name="davaliKusur" value={form.davaliKusur} onChange={handleChange} min="0" max="100" step="0.01" />
              </label>
              <label>
                Olay Tarihi
                <input type="date" name="olayTarihi" value={form.olayTarihi} onChange={handleChange} required />
              </label>
              <label>
                Kazalı Doğ. Tarihi
                <div className="th-dogum-wrap">
                  <input type="date" name="dogumTarihi" value={form.dogumTarihi} onChange={handleChange} required />
                  <select name="cinsiyet" value={form.cinsiyet} onChange={handleChange} aria-label="Cinsiyet">
                    <option value="E">E</option>
                    <option value="K">K</option>
                  </select>
                </div>
              </label>
              <label>
                Adı
                <input type="text" name="adSoyad" value={form.adSoyad} onChange={handleChange} placeholder="İsteğe bağlı" />
              </label>
            </div>

            <div className="th-col">
              <label>
                Rapor / Hesap Tarihi
                <input type="date" name="raporTarihi" value={form.raporTarihi} onChange={handleChange} required />
              </label>
              <label>
                Olay Tarihi Ücret (aylık)
                <input type="number" name="aylikUcret" value={form.aylikUcret} onChange={handleChange} min="0" step="0.01" placeholder={`Boş = asgari (${olayAsgariHint.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL)`} />
                <small style={{ color: '#666', display: 'block', marginTop: 4 }}>
                  Boş bırakılırsa olay tarihindeki net asgari ücret otomatik kullanılır.
                </small>
              </label>
              {isDestek ? (
                <label>
                  Eş/Çocuk İlave AGİ
                  <input type="number" name="ilaveAgi" value={form.ilaveAgi} onChange={handleChange} min="0" step="0.01" />
                </label>
              ) : (
                <label>
                  İş Gücü Kayıp Oranı %
                  <input type="number" name="isGucuKayip" value={form.isGucuKayip} onChange={handleChange} min="0" max="100" step="0.01" />
                </label>
              )}
            </div>

            <div className="th-periods">
              <h3>Dönem Özeti</h3>
              {periodsPreview ? (
                <dl>
                  <div><dt>Olay Tarihi Yaş</dt><dd>{yilYaz(periodsPreview.olayYas)}</dd></div>
                  <div><dt>Bakiye Ömür</dt><dd>{yilYaz(periodsPreview.bakiyeOmur)} yıl</dd></div>
                  <div><dt>Faal Çalışma</dt><dd>{yilYaz(periodsPreview.faalCalisma)} yıl</dd></div>
                  <div><dt>Geçmiş (gün)</dt><dd>{yilYaz(periodsPreview.gecmisGun)}</dd></div>
                  <div><dt>Geçmiş Dönem</dt><dd>{yilYaz(periodsPreview.gecmisYil)} yıl</dd></div>
                  <div><dt>Gelecek Aktif</dt><dd>{yilYaz(periodsPreview.gelecekAktifYil)} yıl</dd></div>
                  <div><dt>Gelecek Pasif</dt><dd>{yilYaz(periodsPreview.gelecekPasifYil)} yıl</dd></div>
                </dl>
              ) : (
                <p className="th-muted">Tarihleri girince dönemler hesaplanır.</p>
              )}
            </div>
          </div>

          {isDestek && (
            <fieldset className="th-params">
              <legend>Destek Alacaklar (doğum tarihi girilenler hesaba dahil)</legend>
              <div className="th-deps">
                <label className="th-check-row">
                  <input type="checkbox" name="esVar" checked={form.esVar} onChange={handleChange} />
                  Eş
                </label>
                {form.esVar && (
                  <div className="th-dogum-wrap">
                    <input type="date" value={form.esDogum} onChange={(e) => setForm((p) => ({ ...p, esDogum: e.target.value }))} />
                    <select value={form.esCinsiyet} onChange={(e) => setForm((p) => ({ ...p, esCinsiyet: e.target.value }))}>
                      <option value="K">K</option>
                      <option value="E">E</option>
                    </select>
                    <input type="number" className="th-psd" min="0" step="0.01" placeholder="SGK PSD (−)" aria-label="Eşin SGK peşin sermaye değeri"
                      value={form.esPsd || ''} onChange={(e) => setForm((p) => ({ ...p, esPsd: e.target.value }))} />
                  </div>
                )}

                {form.cocuklar.map((c, i) => (
                  <div className="th-cocuk-row" key={i}>
                    <span>{i + 1}. Çocuk</span>
                    <input type="date" value={c.dogumTarihi} onChange={(e) => updateCocuk(i, 'dogumTarihi', e.target.value)} />
                    <select value={c.cinsiyet} onChange={(e) => updateCocuk(i, 'cinsiyet', e.target.value)}>
                      <option value="E">E</option>
                      <option value="K">K</option>
                    </select>
                    <label className="th-check-row">
                      <input
                        type="checkbox"
                        checked={!!c.yuksekOgrenim}
                        onChange={(e) => updateCocuk(i, 'yuksekOgrenim', e.target.checked)}
                      />
                      Üni.
                    </label>
                    <input type="number" className="th-psd" min="0" step="0.01" placeholder="SGK PSD (−)" aria-label={`${i + 1}. çocuğun SGK peşin sermaye değeri`}
                      value={c.psd || ''} onChange={(e) => updateCocuk(i, 'psd', e.target.value)} />
                    {form.cocuklar.length > 1 && (
                      <button type="button" className="th-btn-mini" onClick={() => removeCocuk(i)}>Sil</button>
                    )}
                  </div>
                ))}
                {form.cocuklar.length < 5 && (
                  <button type="button" className="th-btn-mini" onClick={addCocuk}>+ Çocuk</button>
                )}

                <label className="th-check-row">
                  <input type="checkbox" name="anneVar" checked={form.anneVar} onChange={handleChange} />
                  Anne
                </label>
                {form.anneVar && (
                  <div className="th-dogum-wrap">
                    <input type="date" value={form.anneDogum} onChange={(e) => setForm((p) => ({ ...p, anneDogum: e.target.value }))} />
                    <input type="number" className="th-psd" min="0" step="0.01" placeholder="SGK PSD (−)" aria-label="Annenin SGK peşin sermaye değeri"
                      value={form.annePsd || ''} onChange={(e) => setForm((p) => ({ ...p, annePsd: e.target.value }))} />
                  </div>
                )}
                <label className="th-check-row">
                  <input type="checkbox" name="babaVar" checked={form.babaVar} onChange={handleChange} />
                  Baba
                </label>
                {form.babaVar && (
                  <div className="th-dogum-wrap">
                    <input type="date" value={form.babaDogum} onChange={(e) => setForm((p) => ({ ...p, babaDogum: e.target.value }))} />
                    <input type="number" className="th-psd" min="0" step="0.01" placeholder="SGK PSD (−)" aria-label="Babanın SGK peşin sermaye değeri"
                      value={form.babaPsd || ''} onChange={(e) => setForm((p) => ({ ...p, babaPsd: e.target.value }))} />
                  </div>
                )}
                <p className="th-note">
                  SGK PSD: Yalnız rücu edilebilen gelir düşülür — örneğin olay iş kazasıysa bağlanan gelirin peşin sermaye değeri.
                  Ölüm sigortasından bağlanan dul/yetim aylığı düşülmez (TBK m.55). Bilinmiyorsa boş bırakın.
                </p>
              </div>
            </fieldset>
          )}

          {!isDestek && (
            <fieldset className="th-params">
              <legend>Geçici İşgöremezlik / Bakıcı</legend>
              <div className="th-params-grid">
                <label className="th-check-row" style={{ gridColumn: '1 / -1' }}>
                  <input type="checkbox" name="geciciHesapla" checked={!!form.geciciHesapla} onChange={handleChange} />
                  Geçici işgöremezlik hesapla
                </label>
                {form.geciciHesapla && (
                  <>
                    <label>
                      Geçici gün
                      <input type="number" name="geciciGun" value={form.geciciGun} onChange={handleChange} min="0" step="1" />
                    </label>
                    <label>
                      GİÖ oranı %
                      <input type="number" name="geciciOran" value={form.geciciOran} onChange={handleChange} min="0" max="100" step="0.01" />
                    </label>
                  </>
                )}
                <label className="th-check-row" style={{ gridColumn: '1 / -1' }}>
                  <input type="checkbox" name="bakiciHesapla" checked={!!form.bakiciHesapla} onChange={handleChange} />
                  Bakıcı gideri hesapla (asgari ücret üzerinden)
                </label>
                {form.bakiciHesapla && (
                  <>
                    <label>
                      Bakıcı süresi
                      <select name="bakiciTur" value={form.bakiciTur} onChange={handleChange}>
                        <option value="gun">Belirli gün (geçici)</option>
                        <option value="surekli">Ömür boyu (sürekli)</option>
                      </select>
                    </label>
                    {form.bakiciTur !== 'surekli' && (
                      <label>
                        Bakıcı gün
                        <input type="number" name="bakiciGun" value={form.bakiciGun} onChange={handleChange} min="0" step="1" />
                      </label>
                    )}
                    <label>
                      Bakım ihtiyacı %
                      <input type="number" name="bakiciOran" value={form.bakiciOran} onChange={handleChange} min="0" max="100" step="0.01" />
                    </label>
                    <label>
                      Ücret esası
                      <select name="bakiciUcret" value={form.bakiciUcret} onChange={handleChange}>
                        <option value="brut">Brüt asgari ücret</option>
                        <option value="net">Net asgari ücret</option>
                      </select>
                    </label>
                    <p className="th-note" style={{ gridColumn: '1 / -1' }}>
                      Yargıtay kararları brüt/net konusunda çelişkili (10. HD 2022 brüt). Tam bağımlı %100, kısmi bağımlı %50 yazılır;
                      ihtiyaç Kurul raporundaki gibi.
                    </p>
                  </>
                )}
              </div>
            </fieldset>
          )}

          <fieldset className="th-params">
            <legend>Kullanılan Parametreler (Değiştirilebilir)</legend>
            <div className="th-params-grid">
              <label>
                Gelir Artış Oranı %
                <input type="number" name="artisOrani" value={form.artisOrani} onChange={handleChange} step="0.01" />
              </label>
              <label>
                İskonto Oranı %
                <input type="number" name="iskontoOrani" value={form.iskontoOrani} onChange={handleChange} step="0.01" />
              </label>
              <label>
                Aktif Başlama (Yaş)
                <input type="number" name="aktifBaslangicYas" value={form.aktifBaslangicYas} onChange={handleChange} min="0" />
              </label>
              <label>
                Aktif Bitiş (Yaş) — Yargıtay: 60
                <input type="number" name="aktifBitisYas" value={form.aktifBitisYas} onChange={handleChange} min="0" disabled={!!form.geliriYok} />
              </label>
              <label>
                Pasif Aylık Gelir (opsiyonel)
                <input type="number" name="pasifAylikUcret" value={form.pasifAylikUcret} onChange={handleChange} min="0" step="0.01" placeholder="Boş = AGİ'siz net asgari ücret" />
              </label>
              <label className="th-check-row" style={{ gridColumn: '1 / -1' }}>
                <input type="checkbox" name="geliriYok" checked={!!form.geliriYok} onChange={handleChange} />
                Düzenli geliri yoktu (ev hanımı, emekli, öğrenci): bütün süre pasif dönem sayılsın
              </label>
              {!isDestek && (
                <label>
                  Trafik Sigortası Ödeme
                  <input type="number" name="sigortaOdemesi" value={form.sigortaOdemesi} onChange={handleChange} min="0" step="0.01" />
                </label>
              )}
            </div>
          </fieldset>

          {error && <div className="th-error">{error}</div>}

          <div className="th-actions">
            <button type="button" className="th-btn secondary" onClick={handleReset}>Formu Sıfırla</button>
            <button type="submit" className="th-btn primary">Tazminat Hesapla</button>
          </div>
          {exportBar}

          {result?.satirlar?.length > 0 && (
            <details className="th-donem">
              <summary>Dönem dönem hesap tablosu ({result.satirlar.length} satır)</summary>
              <div className="th-donem-kaydir">
                {result.type === 'destek' ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Dönem</th><th>Ay</th><th>Aylık gelir</th>
                        {result.dagilim.map((d) => <th key={d.sira}>{d.ad}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {result.satirlar.map((s) => (
                        <tr key={s.bas} className={s.donem === 'gelecek' ? 'gelecek' : ''}>
                          <td>{trTarih(s.bas)} – {trTarih(s.son)}{s.pasif ? ' (pasif)' : ''}</td>
                          <td>{String(s.ay).replace('.', ',')}</td>
                          <td>{formatTL(s.aylik)}</td>
                          {result.dagilim.map((d) => {
                            const p = s.paylar[d.sira];
                            return <td key={d.sira}>{p ? <>{formatTL(p.tutar)} <small>{p.pay}</small></> : '—'}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table>
                    <thead>
                      <tr><th>Kalem</th><th>Dönem</th><th>Ay</th><th>Aylık gelir</th><th>Kayıp</th><th>Tutar</th></tr>
                    </thead>
                    <tbody>
                      {result.satirlar.map((s) => (
                        <tr key={`${s.tur}-${s.bas}`} className={s.tur.startsWith('Bilinmeyen') ? 'gelecek' : ''}>
                          <td>{s.tur}</td>
                          <td>{trTarih(s.bas)} – {trTarih(s.son)}</td>
                          <td>{String(s.ay).replace('.', ',')}</td>
                          <td>{formatTL(s.aylik)}</td>
                          <td>%{String(s.oran).replace('.', ',')}</td>
                          <td>{formatTL(s.tutar)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <p className="th-note">
                Tablodaki tutarlar kusur indiriminden, evlenme indiriminden ve SGK düşümünden öncedir.
                Açık renkli satırlar hesap tarihinden sonraki (bilinmeyen) dönemdir.
              </p>
            </details>
          )}
        </div>

        <aside className="th-results">
          <h2>Sonuçlar</h2>
          {result ? (
            <>
              {form.adSoyad && <p className="th-result-name">{form.adSoyad}</p>}
              <p className="th-muted th-gelir-notu">
                Kazalının aylık geliri: olay tarihinde {formatTL(result.gelirOlay)}, hesap tarihinde {formatTL(result.gelirHesap)}.
                Bakiye ömür {String(result.bakiyeOmur).replace('.', ',')} yıl ({trTarih(result.hayatSonu)} tarihine kadar).
                {result.kusurOrani < 100 ? ` Davalı kusuru %${result.kusurOrani} uygulandı.` : ''}
              </p>
              {result.type === 'destek' ? (
                <>
                  <p className="th-section-label">Hak sahipleri</p>
                  {(result.dagilim || []).map((d) => (
                    <div className="th-kisi" key={d.ad}>
                      <div className="th-result-row">
                        <span>{d.ad} <small>(destek {trTarih(d.destekBitis)} tarihine kadar)</small></span>
                        <strong>{formatTL(d.toplam)}</strong>
                      </div>
                      <div className="th-kisi-detay">
                        <span>Bilinen dönem {formatTL(d.gecmis)}</span>
                        <span>Gelecek {formatTL(d.gelecek)}</span>
                        {d.evlenmeIndirimi > 0 && <span>Evlenme ihtimali %{d.evlenmeIhtimali}: −{formatTL(d.evlenmeIndirimi)}</span>}
                        {d.sgkPsd > 0 && <span>SGK PSD: −{formatTL(d.sgkPsd)}</span>}
                      </div>
                    </div>
                  ))}
                  <div className="th-result-total">
                    <span>GENEL TOPLAM</span>
                    <strong>{formatTL(result.genelToplam)}</strong>
                  </div>
                </>
              ) : (
                <>
                  <div className="th-result-row">
                    <span>Bilinen (geçmiş) dönem</span>
                    <strong>{formatTL(result.gecmis)}</strong>
                  </div>
                  <div className="th-result-row">
                    <span>Gelecek (Aktif) Dönem</span>
                    <strong>{formatTL(result.gelecekAktif)}</strong>
                  </div>
                  <div className="th-result-row">
                    <span>Gelecek (Pasif) Dönem</span>
                    <strong>{formatTL(result.gelecekPasif)}</strong>
                  </div>
                  {result.geciciIsgoremezlik > 0 && (
                    <div className="th-result-row">
                      <span>Geçici İşgöremezlik</span>
                      <strong>{formatTL(result.geciciIsgoremezlik)}</strong>
                    </div>
                  )}
                  {result.bakiciGideri > 0 && (
                    <div className="th-result-row">
                      <span>Bakıcı Gideri</span>
                      <strong>{formatTL(result.bakiciGideri)}</strong>
                    </div>
                  )}
                  {result.sigortaMahsup > 0 && (
                    <div className="th-result-row muted">
                      <span>Sigorta Mahsup (−)</span>
                      <strong>{formatTL(result.sigortaMahsup)}</strong>
                    </div>
                  )}
                  <div className="th-result-total">
                    <span>TOPLAM</span>
                    <strong>{formatTL(result.toplam)}</strong>
                  </div>
                </>
              )}
              {(result.uyarilar || []).map((u) => <p key={u} className="th-uyari">{u}</p>)}
              <p className="th-disclaimer">
                Yargıtay uygulamasıyla: TRH-2010 yaşam tablosu; bilinen dönem gerçek (net) asgari ücretle, gelecek dönem hesap
                tarihindeki gelirle progresif rant (%10 artış / %10 iskonto); aktif dönem 18–60 yaş, pasif dönem AGİ'siz net asgari
                ücret; destek payları dönem dönem yeniden dağıtılır. Ücret girilmezse asgari ücret; girilirse asgari ücretin artış
                oranında güncellenir. Bilgilendirme amaçlıdır; bilirkişi raporunun yerine geçmez.
              </p>
            </>
          ) : (
            <p className="th-muted">Hesap sonucu burada görünür.</p>
          )}
        </aside>
      </form>
    </div>
  );
};

export default TazminatHesap;
